import { Link } from 'react-router';
import './FaqPage.css';

function FaqPage() {
    return (
        <div className="faq-page">
            <div className="faq-inner">

                <div className="faq-eyebrow">Help</div>
                <h1 className="faq-title">Frequently Asked Questions</h1>

                {/* ── 1. Getting Started ── */}
                <section className="faq-section">
                    <h2 className="faq-h2">Getting Started</h2>

                    <details className="faq-item">
                        <summary className="faq-question">What is CertDrill?</summary>
                        <div className="faq-answer">
                            <p>CertDrill is an AI-powered active recall platform for IT certification exam prep. Instead of passively reading flashcards, you type free-text answers and AI grades how well your response captures the correct concept.</p>
                        </div>
                    </details>

                    <details className="faq-item">
                        <summary className="faq-question">Do I need an account?</summary>
                        <div className="faq-answer">
                            <p>Yes. Sign in with Microsoft to save your study progress, confidence ratings, and question history across sessions.</p>
                        </div>
                    </details>
                </section>

                {/* ── 2. The Study Loop ── */}
                <section className="faq-section">
                    <h2 className="faq-h2">The Study Loop</h2>

                    <details className="faq-item">
                        <summary className="faq-question">How do I answer questions?</summary>
                        <div className="faq-answer">
                            <p>Type your answer in the text box and click Submit. You don't need to match the exact wording — AI scores how well your answer captures the correct concept.</p>
                        </div>
                    </details>

                    <details className="faq-item">
                        <summary className="faq-question">How is my answer graded?</summary>
                        <div className="faq-answer">
                            <p>Your response is compared to the correct answer using OpenAI embeddings (semantic similarity). A score ≥ 0.75 is marked correct. You can see the score after submitting.</p>
                        </div>
                    </details>

                    <details className="faq-item">
                        <summary className="faq-question">What is the confidence rating?</summary>
                        <div className="faq-answer">
                            <p>After submitting, you rate how confident you felt: <strong>Low / Medium / High</strong>. CertDrill uses this to prioritise which questions to show you next — low-confidence questions appear more often.</p>
                        </div>
                    </details>

                    <details className="faq-item">
                        <summary className="faq-question">Why do certain questions keep appearing?</summary>
                        <div className="faq-answer">
                            <p>The study loop surfaces questions you marked low-confidence or haven't answered recently. Once you consistently rate a question High confidence, it appears less frequently.</p>
                        </div>
                    </details>

                    <details className="faq-item">
                        <summary className="faq-question">Can I skip a question?</summary>
                        <div className="faq-answer">
                            <p>Yes. Click Skip to move on without it counting against your daily progress.</p>
                        </div>
                    </details>
                </section>

                {/* ── 3. Topics, Groups & Domains ── */}
                <section className="faq-section">
                    <h2 className="faq-h2">Topics, Groups &amp; Domains</h2>

                    <details className="faq-item">
                        <summary className="faq-question">What is a Domain / Group / Topic?</summary>
                        <div className="faq-answer">
                            <p>Content is organised in three tiers: <strong>Domain</strong> (e.g. "Cloud") → <strong>Group</strong> (e.g. "AWS") → <strong>Topic</strong> (e.g. "AWS Solutions Architect Associate"). Questions belong to a Topic.</p>
                        </div>
                    </details>

                    <details className="faq-item">
                        <summary className="faq-question">Where do questions come from?</summary>
                        <div className="faq-answer">
                            <p>Questions are generated by GPT based on the official exam guide for each topic. They are deduplicated automatically so you won't see near-identical questions.</p>
                        </div>
                    </details>

                    <details className="faq-item">
                        <summary className="faq-question">How do I generate more questions?</summary>
                        <div className="faq-answer">
                            <p>On the study page, click <strong>Generate New Question</strong> when you've answered all questions in a topic. There's also a <strong>Batch Generate</strong> option to create several at once. Each topic has a maximum question target; once reached the button is hidden.</p>
                        </div>
                    </details>

                    <details className="faq-item">
                        <summary className="faq-question">Can I add my own topics?</summary>
                        <div className="faq-answer">
                            <p>Yes. Use the <strong>Add Topic</strong> field in the sidebar. Provide the certification name, vendor, and exam number.</p>
                        </div>
                    </details>
                </section>

                {/* ── 4. Study Everything Mode ── */}
                <section className="faq-section">
                    <h2 className="faq-h2">✦ Study Everything Mode</h2>

                    <details className="faq-item">
                        <summary className="faq-question">What is Study Everything?</summary>
                        <div className="faq-answer">
                            <p>Select <strong>✦ Study Everything</strong> from the domain dropdown to study across all topics at once. CertDrill picks questions you haven't seen recently or rated low-confidence, regardless of which topic they belong to.</p>
                        </div>
                    </details>

                    <details className="faq-item">
                        <summary className="faq-question">Why do questions show a "Domain · Topic" badge?</summary>
                        <div className="faq-answer">
                            <p>In Study Everything mode, questions come from different topics, so the badge tells you which certification each question is for.</p>
                        </div>
                    </details>

                    <details className="faq-item">
                        <summary className="faq-question">Can I generate new questions in Study Everything mode?</summary>
                        <div className="faq-answer">
                            <p>No. Generation requires a specific topic to be selected. Switch to a topic to generate new questions.</p>
                        </div>
                    </details>
                </section>

                {/* ── 5. Lab Guides & Further Learning ── */}
                <section className="faq-section">
                    <h2 className="faq-h2">Lab Guides &amp; Further Learning</h2>

                    <details className="faq-item">
                        <summary className="faq-question">What is a Lab Guide?</summary>
                        <div className="faq-answer">
                            <p>After answering a question, you can generate a hands-on lab exercise to practice the concept in a real environment. Click <strong>Generate Lab Guide</strong> in the Further Learning section.</p>
                        </div>
                    </details>

                    <details className="faq-item">
                        <summary className="faq-question">What are the YouTube videos?</summary>
                        <div className="faq-answer">
                            <p>CertDrill automatically finds relevant YouTube videos for each question — one long-form explainer and one short-form clip. These appear in the Further Learning section after you answer.</p>
                        </div>
                    </details>

                    <details className="faq-item">
                        <summary className="faq-question">How do I rate a video?</summary>
                        <div className="faq-answer">
                            <p>Use the thumbs up / down icons below each video. Highly downvoted videos are flagged for review and replaced automatically.</p>
                        </div>
                    </details>
                </section>

                {/* ── 6. Comments ── */}
                <section className="faq-section">
                    <h2 className="faq-h2">Comments</h2>

                    <details className="faq-item">
                        <summary className="faq-question">Can I discuss questions with others?</summary>
                        <div className="faq-answer">
                            <p>Yes. A comment section appears under each question in Further Learning. Comments are visible to all users. All comments are moderated automatically before being posted.</p>
                        </div>
                    </details>
                </section>

                {/* ── 7. Account & Progress ── */}
                <section className="faq-section">
                    <h2 className="faq-h2">Account &amp; Progress</h2>

                    <details className="faq-item">
                        <summary className="faq-question">Where can I see my progress?</summary>
                        <div className="faq-answer">
                            <p>Visit the Profile page (top-right menu) for stats: total questions answered, accuracy rate, average confidence, study days, and active goals.</p>
                        </div>
                    </details>

                    <details className="faq-item">
                        <summary className="faq-question">What are Study Goals?</summary>
                        <div className="faq-answer">
                            <p>You can set a target date and topic on the Profile page (or directly in the study sidebar). CertDrill tracks whether you're on pace to complete it and shows an Exam Readiness bar.</p>
                        </div>
                    </details>

                    <details className="faq-item">
                        <summary className="faq-question">How do I sign out?</summary>
                        <div className="faq-answer">
                            <p>Click <strong>Sign Out</strong> in the top-right corner of the navigation bar.</p>
                        </div>
                    </details>
                </section>

                <div className="faq-back">
                    <Link to="/" className="faq-back-link">← Back to home</Link>
                </div>

            </div>
        </div>
    );
}

export default FaqPage;
